import { useTranslation } from 'react-i18next';
import { MapPin, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { CategoryChip } from '../../ui-components/category/CategoryChip';
import FieldStrengthChip from '../FieldStrengthChip';
import { MRIMachinePopulated } from '../types';

interface RoomPanelHeaderProps {
    machine: MRIMachinePopulated;
    imageUrl: string | null;
    readOnly: boolean;
    onEdit?: () => void;
    onDelete: () => void;
}

export default function RoomPanelHeader({
    machine,
    imageUrl,
    readOnly,
    onEdit,
    onDelete,
}: RoomPanelHeaderProps) {
    const { t } = useTranslation();

    return (
        <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-4">
                {imageUrl && (
                    <img
                        src={imageUrl}
                        alt={machine.Name}
                        className="h-20 w-20 shrink-0 rounded-md object-cover"
                    />
                )}
                <div className="flex min-w-0 flex-col gap-2">
                    <h1 className="truncate text-lg font-semibold leading-tight tracking-tight">
                        {machine.Name}
                    </h1>
                    <div className="flex min-w-0 items-center gap-3">
                        <p className="truncate text-sm">
                            {machine.Manufacturer && (
                                <span>{machine.Manufacturer} </span>
                            )}
                            <span className="font-medium">{machine.Model}</span>
                        </p>
                        <FieldStrengthChip
                            fieldStrength={machine.FieldStrength}
                            className="shrink-0"
                        />
                    </div>
                    {(machine.category || machine.location) && (
                        <div className="flex min-w-0 items-center gap-3 text-xs font-medium text-subtle-foreground">
                            {machine.category && (
                                <CategoryChip category={machine.category} />
                            )}
                            {machine.location && (
                                <span className="flex min-w-0 items-center gap-1.5">
                                    <MapPin
                                        className="h-3.5 w-3.5 shrink-0"
                                        aria-hidden="true"
                                    />
                                    <span className="truncate">
                                        {machine.location}
                                    </span>
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {!readOnly && (
                <div className="flex shrink-0 items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        className="[&_svg]:!size-3.5"
                        onClick={onEdit}
                    >
                        <Pencil aria-hidden="true" />
                        {t('roomOverview.machineDetail.edit')}
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive [&_svg]:!size-3.5"
                        onClick={onDelete}
                    >
                        <Trash2 aria-hidden="true" />
                        {t('roomOverview.machineDetail.delete')}
                    </Button>
                </div>
            )}
        </div>
    );
}
